import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { CheckCircle, Users, ShoppingCart, CreditCard, Truck, Star, Loader2, Send, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const storeTypes = [
  { title: "Sports Teams", desc: "Individual players order and pay their own jersey, uniform, or warm-up. No more collecting checks from parents.", icon: "Trophy" },
  { title: "Corporate Groups", desc: "Send a store link to your whole company. Everyone selects their size and pays — you just review and approve.", icon: "Briefcase" },
  { title: "Schools & PTAs", desc: "Run spirit wear sales without managing money. Students order and pay online, you collect a report.", icon: "GraduationCap" },
  { title: "Events & Fundraisers", desc: "Open a store for your event or fundraiser. Great for charity runs, family reunions, and class reunions.", icon: "Calendar" },
];

const howItWorks = [
  { step: "01", title: "Tell Us About Your Store", desc: "Fill out our team store setup form with your organization details, what you're ordering, and your deadline." },
  { step: "02", title: "We Build Your Store", desc: "Our team sets up a custom branded online store with your products, sizes, and pricing — ready in 24-48 hours." },
  { step: "03", title: "Share the Link", desc: "Send the store link to your team, employees, or members. Each person orders and pays for their own items." },
  { step: "04", title: "We Print & Deliver", desc: "After the store closes, we produce all orders at once and deliver them bundled for easy distribution." },
];

const benefits = [
  "No money collection — everyone pays individually",
  "Custom online store branded with your logo",
  "Multiple products and size options available",
  "Open for a set window (1 week, 2 weeks, etc.)",
  "Automatic size chart and product info",
  "Order report emailed when store closes",
  "Free setup for orders of 24+ expected pieces",
  "Rush store setup available (48 hours)",
];

function TeamStoreForm() {
  const [status, setStatus] = useState("idle");
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        ...data,
        service_type: "Team Store Setup",
        design_status: data.design_status || "I have a logo/image but need print prep",
        quantity: data.expected_size || "48-71 pieces",
        message: `TEAM STORE REQUEST — Organization: ${data.company || "N/A"}, Store Type: ${data.store_type}, Expected Size: ${data.expected_size}, Deadline: ${data.needed_by || "N/A"}. Notes: ${data.message || "None"}`,
      });
      setStatus("success");
      reset();
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="team-store-success" className="text-center py-12">
        <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-500" />
        </div>
        <h3 className="font-teko text-3xl text-white uppercase mb-2">Request Received!</h3>
        <p className="text-neutral-400 font-inter text-sm max-w-sm mx-auto">We'll set up your team store preview within 24-48 hours and send you a link to review.</p>
        <button onClick={() => setStatus("idle")} className="mt-6 text-sm text-red-500 hover:text-red-400 font-inter uppercase tracking-widest">
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid="team-store-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Full Name *</label>
          <input data-testid="ts-name" {...register("full_name", { required: true })} className="ninja-input" placeholder="Coach Smith" />
          {errors.full_name && <p className="text-red-500 text-xs mt-1">Required</p>}
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Email *</label>
          <input data-testid="ts-email" type="email" {...register("email", { required: true })} className="ninja-input" placeholder="coach@school.com" />
          {errors.email && <p className="text-red-500 text-xs mt-1">Required</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Phone *</label>
          <input data-testid="ts-phone" {...register("phone", { required: true })} className="ninja-input" placeholder="(214) 555-0000" />
          {errors.phone && <p className="text-red-500 text-xs mt-1">Required</p>}
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Organization Name *</label>
          <input data-testid="ts-org" {...register("company", { required: true })} className="ninja-input" placeholder="Dallas FC Youth Soccer" />
          {errors.company && <p className="text-red-500 text-xs mt-1">Required</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Store Type *</label>
          <select data-testid="ts-type" {...register("store_type", { required: true })} className="ninja-input appearance-none cursor-pointer">
            <option value="">Select type...</option>
            {["Sports Team", "Corporate / Company", "School / PTA", "Church / Nonprofit", "Event / Fundraiser", "Other"].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {errors.store_type && <p className="text-red-500 text-xs mt-1">Required</p>}
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Expected # of Orders</label>
          <select data-testid="ts-size" {...register("expected_size")} className="ninja-input appearance-none cursor-pointer">
            <option value="">Estimated quantity...</option>
            {["Under 12", "12-24", "25-50", "51-100", "101-250", "250+", "Not Sure"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Store Close Date (Deadline)</label>
          <input data-testid="ts-deadline" type="date" {...register("needed_by")} className="ninja-input" />
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Do You Have a Logo?</label>
          <select data-testid="ts-design" {...register("design_status")} className="ninja-input appearance-none cursor-pointer">
            <option value="">Artwork status...</option>
            {["Yes — print-ready AI/EPS/PDF file", "Yes — a PNG or JPG logo", "No — I need design help", "Starting from scratch"].map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">What Are You Looking to Print?</label>
        <textarea data-testid="ts-message" {...register("message")} rows={3} className="ninja-input resize-none" placeholder="e.g. Soccer jerseys with names and numbers, hoodies, warm-up pants..." />
      </div>
      {status === "error" && (
        <p className="text-red-400 text-sm font-inter">Something went wrong. Please try again or call (214) 555-0190.</p>
      )}
      <button type="submit" data-testid="ts-submit-btn" disabled={status === "loading"} className="w-full ninja-btn-primary py-4 font-teko tracking-widest text-lg flex items-center justify-center gap-2 disabled:opacity-50">
        {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> SETTING UP...</> : <><Send size={16} /> REQUEST TEAM STORE SETUP</>}
      </button>
      <p className="text-center text-xs text-neutral-600 font-inter">Free setup on 24+ expected orders. Store live within 24-48 hours.</p>
    </form>
  );
}

export default function TeamStore() {
  return (
    <>
      <Helmet>
        <title>Team Store Setup Dallas TX | Online Group Ordering for Sports, Schools & Corporate | Ninja Screen Print</title>
        <meta name="description" content="Set up a custom online team store for your sports team, school, or company. Everyone orders and pays individually. Ninja Screen Print handles setup, printing, and delivery in DFW." />
      </Helmet>

      {/* Hero */}
      <div
        className="pt-16 relative min-h-[50vh] flex items-end pb-16 overflow-hidden"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&q=80')", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="relative z-10 ninja-container">
          <div className="red-line" />
          <h1 className="font-teko text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tight leading-tight">
            Team Stores<br /><span className="text-red-500">Made Simple</span>
          </h1>
          <p className="text-neutral-300 font-inter mt-4 text-lg max-w-2xl">
            Your own custom online store where everyone orders and pays individually. No money collection. No size spreadsheets. Just click, order, done.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            {["Free store setup (24+ orders)", "Live in 24-48 hours", "Everyone pays individually", "Free DFW delivery"].map(item => (
              <div key={item} className="flex items-center gap-2 text-sm text-neutral-300 font-inter">
                <CheckCircle size={12} className="text-red-500" /> {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Store Types */}
      <section className="ninja-section" data-testid="store-types">
        <div className="ninja-container">
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">Perfect for Every Group</h2>
            <p className="text-neutral-400 font-inter mt-2">Team stores solve the #1 headache in group ordering — collecting money and sizes.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {storeTypes.map((type) => (
              <div key={type.title} data-testid={`store-type-${type.title}`} className="ninja-card p-6 text-center">
                <div className="w-12 h-12 bg-red-600/10 border border-red-600/30 flex items-center justify-center mx-auto mb-4">
                  <Users size={20} className="text-red-500" />
                </div>
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-2">{type.title}</h3>
                <p className="text-neutral-400 font-inter text-sm leading-relaxed">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="how-it-works-section">
        <div className="ninja-container">
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">How Team Stores Work</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, i) => (
              <div key={item.step} data-testid={`ts-step-${item.step}`} className="relative">
                {i < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-full h-px bg-neutral-800 translate-x-1/2 z-0" />
                )}
                <div className="ninja-card p-6 relative z-10">
                  <div className="w-12 h-12 bg-red-600 flex items-center justify-center mb-4 font-teko text-xl text-white">{item.step}</div>
                  <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-2">{item.title}</h3>
                  <p className="text-neutral-400 font-inter text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Process Icons */}
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg mx-auto">
            {[
              { icon: ShoppingCart, label: "Custom Store Created" },
              { icon: CreditCard, label: "Individual Payments" },
              { icon: Truck, label: "Bundled Delivery" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="text-center">
                <div className="w-12 h-12 border border-neutral-700 flex items-center justify-center mx-auto mb-2">
                  <Icon size={20} className="text-red-500" />
                </div>
                <div className="text-xs text-neutral-400 font-inter">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="ninja-section" data-testid="benefits-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6">
                Everything Included.<br />No Headaches.
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {benefits.map((b) => (
                  <div key={b} className="flex items-start gap-2 py-2 border-b border-neutral-900">
                    <CheckCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-300 font-inter">{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ninja-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-600 flex items-center justify-center">
                  <Star size={16} className="text-white fill-white" />
                </div>
                <div>
                  <div className="font-teko text-xl text-white uppercase">Coach Williams</div>
                  <div className="text-xs text-neutral-500 font-inter">Frisco FC Youth Soccer</div>
                </div>
              </div>
              <p className="text-neutral-300 font-inter text-sm italic leading-relaxed mb-4">
                "Before the team store, I was chasing 24 parents for sizes and checks for 3 weeks. Ninja Print set up a store in 2 days, every parent ordered and paid online, and the jerseys were delivered all together. I'll never go back to the old way."
              </p>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-red-500 fill-red-500" />)}
              </div>
              <div className="mt-6 pt-6 border-t border-neutral-800 space-y-2">
                {["Sports team of 24 jerseys", "Store open for 1 week", "All 24 parents ordered & paid online", "Delivered to the coach bundled by size"].map(item => (
                  <div key={item} className="flex items-center gap-2 text-xs text-neutral-400 font-inter">
                    <ArrowRight size={10} className="text-red-500" /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="team-store-form-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4">
                Set Up Your Team Store
              </h2>
              <p className="text-neutral-400 font-inter leading-relaxed mb-6">
                Tell us about your group and what you're looking to order. We'll have your custom store live within 24-48 hours, ready to share with your team.
              </p>
              <div className="space-y-3">
                {[
                  { label: "Response Time", value: "24-48 hours" },
                  { label: "Store Duration", value: "1-4 weeks (you choose)" },
                  { label: "Min. Orders for Free Setup", value: "24+ expected pieces" },
                  { label: "Payment Collection", value: "Per individual (Stripe secure)" },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center border-b border-neutral-800 pb-2">
                    <span className="text-sm text-neutral-500 font-inter">{label}</span>
                    <span className="font-teko text-lg text-red-500">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ninja-card p-8">
              <TeamStoreForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
